/**
 * Shared addon props and wrapper for Rhf* input adapters.
 *
 * Any adapter that renders a text-like input can opt in to prefix/suffix
 * label support by spreading `InputAddonProps` into its own props interface
 * and wrapping its input with `withInputAddons(input, props)`.
 *
 * Props mirror InputGroup.AttachedAddon — text labels sit flush inside the
 * input border with a muted pill background. Use for `$`, `%`, `DAYS`,
 * `https://`, `.com`, etc.
 *
 * Usage in a FieldDef:
 * ```ts
 * export const price = define({
 *   adapterHint: 'number',
 *   adapterProps: { startAddon: '$' },
 * });
 *
 * export const electionWindowStart = define({
 *   adapterHint: 'number',
 *   adapterProps: { endAddon: 'DAYS' },
 * });
 * ```
 */
import * as React from 'react';
import { InputGroup } from '../primitives/InputGroup';

export interface InputAddonProps {
  /**
   * Text label attached to the LEFT side of the input.
   * Rendered as `InputGroup.AttachedAddon(position="start")`.
   * E.g. `'$'`, `'https://'`, `'+'`.
   */
  startAddon?: string;
  /**
   * Text label attached to the RIGHT side of the input.
   * Rendered as `InputGroup.AttachedAddon(position="end")`.
   * E.g. `'DAYS'`, `'%'`, `'YEAR'`, `'.com'`.
   */
  endAddon?: string;
}

/**
 * Wraps `input` in an `InputGroup` with the appropriate `AttachedAddon`
 * elements when `startAddon` or `endAddon` are present.
 * Returns the bare `input` unchanged when neither is set.
 */
export function withInputAddons(
  input: React.ReactElement,
  {
    startAddon,
    endAddon,
    error,
    disabled,
  }: InputAddonProps & { error?: boolean; disabled?: boolean },
): React.ReactElement {
  if (!startAddon && !endAddon) return input;

  return (
    <InputGroup error={error} disabled={disabled}>
      {startAddon && (
        <InputGroup.AttachedAddon position="start">{startAddon}</InputGroup.AttachedAddon>
      )}
      {input}
      {endAddon && (
        <InputGroup.AttachedAddon position="end">{endAddon}</InputGroup.AttachedAddon>
      )}
    </InputGroup>
  );
}
